import { useQuery, useMutation, useQueryClient, useInfiniteQuery } from '@tanstack/react-query';
import { api } from '../lib/api.js';
import type { InfiniteListPage } from './useInfiniteList.js';

export interface ReportRow {
  id: number;
  slug: string;
  type: string;
  title: string;
  status: string;
  inputsJson: string;
  dataJson: string | null;
  narrativeJson: string | null;
  htmlPath: string | null;
  contactId: number | null;
  leadId: string | null;
  projectId: number | null;
  taskId: string | null;
  lastError: string | null;
  publicViews: number;
  pdfDownloads: number;
  lastViewedAt: string | null;
  generatedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ReportEventEntry {
  id: number;
  eventType: string;
  createdAt: number;
  hashedIpShort: string | null;
  userAgentSimple: string | null;
  metadata: Record<string, unknown> | null;
}

export interface ReportStats {
  totalViews: number;
  uniqueVisitors: number;
  technicalExpands: number;
  ctaClicks: number;
  avgTimeOnPage: number | null;
  lastViewAt: number | null;
  lastViewAgo: string | null;
}

export interface ReportInternal extends ReportRow {
  data: unknown;
  stats: ReportStats;
  recentEvents: ReportEventEntry[];
  hasMoreEvents: boolean;
}

export interface ReportsResult {
  reports: ReportRow[];
  total: number;
}

export interface ReportFilters {
  type?: string;
  status?: string;
  contactId?: number;
  leadId?: string;
  projectId?: number;
  taskId?: string;
  search?: string;
  page?: number;
  limit?: number;
}

function buildQuery(filters: ReportFilters): string {
  const params = new URLSearchParams();
  if (filters.type) params.set('type', filters.type);
  if (filters.status) params.set('status', filters.status);
  if (filters.contactId != null) params.set('contactId', String(filters.contactId));
  if (filters.leadId) params.set('leadId', filters.leadId);
  if (filters.projectId != null) params.set('projectId', String(filters.projectId));
  if (filters.taskId) params.set('taskId', filters.taskId);
  if (filters.search) params.set('search', filters.search);
  if (filters.page) params.set('page', String(filters.page));
  if (filters.limit) params.set('limit', String(filters.limit));
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

export function useReports(filters: ReportFilters = {}) {
  return useQuery<ReportsResult>({
    queryKey: ['reports', filters],
    queryFn: () => api.get<ReportsResult>(`/api/reports${buildQuery(filters)}`),
  });
}

// Reports producidos por una task (Radar → report, etc.) — link real vía
// reports.task_id, nunca derivado. Usado por TaskDetailPage.
export function useReportsForTask(taskId: string | null) {
  return useQuery<ReportsResult>({
    queryKey: ['reports', 'by-task', taskId],
    queryFn: () => api.get<ReportsResult>(`/api/reports${buildQuery({ taskId: taskId! })}`),
    enabled: taskId != null,
  });
}

export function useInfiniteReports(filters: Omit<ReportFilters, 'page' | 'cursor'> = {}) {
  const limit = filters.limit ?? 50;
  return useInfiniteQuery<InfiniteListPage<ReportRow>, Error, { pages: InfiniteListPage<ReportRow>[] }, unknown[], string | undefined>({
    queryKey: ['reports', 'infinite', filters],
    queryFn: async ({ pageParam }) => {
      const params = new URLSearchParams();
      if (filters.type) params.set('type', filters.type);
      if (filters.status) params.set('status', filters.status);
      if (filters.search) params.set('search', filters.search);
      if (filters.contactId != null) params.set('contactId', String(filters.contactId));
      if (filters.leadId) params.set('leadId', filters.leadId);
      params.set('limit', String(limit));
      if (pageParam) params.set('cursor', pageParam);
      const result = await api.get<ReportsResult>(`/api/reports?${params.toString()}`);
      return { rows: result.reports, total: result.total };
    },
    getNextPageParam: (lastPage) => {
      if (lastPage.rows.length < limit) return undefined;
      const last = lastPage.rows[lastPage.rows.length - 1];
      return last?.createdAt ?? undefined;
    },
    initialPageParam: undefined,
  });
}

export function useReport(id: number | string | null) {
  return useQuery<ReportRow>({
    queryKey: ['report', id],
    queryFn: () => api.get<ReportRow>(`/api/reports/${id}`),
    enabled: id !== null,
    refetchInterval: (query) => {
      const data = query.state.data;
      if (data && (data.status === 'generating' || data.status === 'pending')) return 3000;
      return false;
    },
  });
}

export interface CreateReportInput {
  type: string;
  title: string;
  inputs: Record<string, unknown>;
  contactId?: number;
  leadId?: string;
}

export function useCreateReport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateReportInput) =>
      api.post<{ id: number; slug: string; status: string }>('/api/reports', body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['reports'] });
    },
  });
}

export function useDeleteReport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.delete<{ ok: boolean }>(`/api/reports/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['reports'] });
    },
  });
}

export function useBulkDeleteReports() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids: number[]) => api.delete<{ deleted: number }>('/api/reports/bulk/delete', { ids }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['reports'] });
    },
  });
}

export function useReportInternal(id: number | null) {
  return useQuery<ReportInternal>({
    queryKey: ['report-internal', id],
    queryFn: () => api.get<ReportInternal>(`/api/reports/${id}/internal`),
    enabled: id !== null,
    refetchInterval: 30_000,
  });
}

export function useRegenerateReport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      api.post<{ ok: boolean; status: string }>(`/api/reports/${id}/regenerate`, {}),
    onSuccess: (_data, id) => {
      void qc.invalidateQueries({ queryKey: ['reports'] });
      void qc.invalidateQueries({ queryKey: ['report', id] });
    },
  });
}

